import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { CONFLICT_POLL } from '@/constants/testIds';
import { CheckCircle2, MessageSquare, Loader2, Trophy, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline poll card attached to a single conflict.
 * - If `poll` is null, shows a "Resolve this" button which creates a poll.
 * - Once a poll exists, renders options with live tally + lets the user vote.
 * - When `is_resolved`, replaces options with a winning banner.
 *
 * Props:
 *   trip            - trip object (needs trip_id, current user's user_id derived from auth, participants)
 *   conflict        - {conflict_id, title, message, category, severity}
 *   poll            - existing poll for this conflict or null
 *   currentUserId   - so we can highlight my vote
 *   onChange(poll)  - notify parent when poll is created/voted/deleted
 */
export default function ConflictPollCard({ trip, conflict, poll, currentUserId, onChange }) {
    const [busy, setBusy] = useState(false);
    const tripId = trip.trip_id;

    const create = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/trips/${tripId}/conflicts/${conflict.conflict_id}/resolve-poll`, {
                conflict_title: conflict.title,
                conflict_category: conflict.category,
                conflict_message: conflict.message,
                conflict_severity: conflict.severity,
            });
            toast.success('Poll created — invite the group to vote');
            onChange?.(data.poll);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not create poll');
        } finally {
            setBusy(false);
        }
    };

    const vote = async (optionKey) => {
        if (!poll) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/trips/${tripId}/polls/${poll.poll_id}/vote`, { option_key: optionKey });
            onChange?.(data.poll);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not record vote');
        } finally {
            setBusy(false);
        }
    };

    const cancel = async () => {
        if (!poll) return;
        if (!window.confirm('Delete this poll? Anyone can start a new one later.')) return;
        setBusy(true);
        try {
            await api.delete(`/trips/${tripId}/polls/${poll.poll_id}`);
            toast.success('Poll deleted');
            onChange?.(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not delete poll');
        } finally {
            setBusy(false);
        }
    };

    if (!poll) {
        return (
            <div className="mt-3">
                <Button
                    onClick={create}
                    disabled={busy}
                    data-testid={CONFLICT_POLL.resolveButton}
                    className="rounded-full h-9 px-4 text-white text-xs"
                    style={{ background: 'var(--ns-navy)' }}
                >
                    {busy ? <Loader2 size={12} className="mr-2 animate-spin" /> : <MessageSquare size={12} className="mr-2" />}
                    Resolve this
                </Button>
            </div>
        );
    }

    const myVote = currentUserId ? (poll.votes || {})[currentUserId] : null;
    const totalParticipants = poll.participants_count || 1;
    const isResolved = poll.is_resolved;
    const resolved = poll.resolved_option;

    return (
        <div data-testid={CONFLICT_POLL.pollCard} className="mt-4 rounded-2xl bg-white border border-[var(--ns-border)] p-4">
            <div className="flex items-center justify-between mb-3">
                <div className="text-xs uppercase tracking-[0.18em] text-[var(--ns-text3)]">
                    {isResolved ? 'Decision' : `Group poll · ${poll.total_votes}/${totalParticipants} voted`}
                </div>
                {!isResolved && (
                    <button
                        type="button"
                        onClick={cancel}
                        disabled={busy}
                        data-testid={CONFLICT_POLL.pollCancel}
                        className="text-[var(--ns-text3)] hover:text-[#D05C45] p-1 rounded-full"
                        title="Delete poll"
                    >
                        <X size={14} />
                    </button>
                )}
            </div>

            {isResolved && resolved ? (
                <div
                    data-testid={CONFLICT_POLL.pollResolved}
                    className="flex items-start gap-3 rounded-xl p-3"
                    style={{ background: 'rgba(74,124,89,0.10)' }}
                >
                    <Trophy size={18} style={{ color: '#4A7C59' }} className="mt-0.5 flex-shrink-0" />
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: '#4A7C59' }}>Group decided</div>
                        <div className="font-display text-lg font-semibold text-[var(--ns-text)]">{resolved.label}</div>
                        <div className="text-xs text-[var(--ns-text2)] mt-0.5">
                            {(poll.tally || {})[resolved.key] || 0} of {totalParticipants} voted for this
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-2">
                    {(poll.options || []).map((opt) => {
                        const count = (poll.tally || {})[opt.key] || 0;
                        const pct = totalParticipants ? Math.round((count / totalParticipants) * 100) : 0;
                        const isMine = myVote === opt.key;
                        return (
                            <button
                                key={opt.key}
                                type="button"
                                onClick={() => vote(opt.key)}
                                disabled={busy}
                                data-testid={CONFLICT_POLL.pollOption}
                                data-option-key={opt.key}
                                data-my-vote={isMine ? 'true' : 'false'}
                                className={`relative w-full text-left rounded-xl border p-3 transition overflow-hidden ${isMine ? 'border-[var(--ns-navy)]' : 'border-[var(--ns-border)] hover:border-[var(--ns-clay)]'}`}
                            >
                                {/* progress bg */}
                                <div
                                    className="absolute inset-y-0 left-0"
                                    style={{ width: `${pct}%`, background: isMine ? 'rgba(29,45,68,0.07)' : 'var(--ns-soft)', transition: 'width 200ms' }}
                                />
                                <div className="relative flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                        {isMine && <CheckCircle2 size={14} className="text-[var(--ns-navy)] flex-shrink-0" />}
                                        <span className="text-sm font-medium text-[var(--ns-text)] truncate">{opt.label}</span>
                                    </div>
                                    <span data-testid={CONFLICT_POLL.pollOptionTally} className="text-xs font-semibold tabular-nums text-[var(--ns-text2)] flex-shrink-0">{count} · {pct}%</span>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="text-[11px] text-[var(--ns-text3)] mt-3 text-center">
                Majority threshold: {poll.majority_threshold} of {totalParticipants} participants
            </div>
        </div>
    );
}
